import React from "react";
import styles from "./PDFExport.module.scss";
import { PesTest, TrainedIndicator } from "generated/graphql";
import { PartialDeep } from "type-fest";
import { format, parseISO } from "date-fns";
import { GetDynamicValue, GetPESIndex } from "@pages/pdf-export/Indices";
import type { PdfIndicatorHeader } from "@pages/pdf-export/Page1Table";
import tinygradient from "tinygradient";
import { IndicatorGradientColors } from "@pages/pdf-export/PDFExport";

interface Page3TableProps {
  allTests: PartialDeep<PesTest, { recurseIntoArrays: true }>[];
  indicatorNamesAndIds: {
    id: string;
    name: string;
    displayName: string;
    trainedIndicator: PartialDeep<TrainedIndicator>;
  }[];
  /** Headers to render; defaults to every chemical indicator. */
  headers?: PdfIndicatorHeader[];
}

export const page3Headers: PdfIndicatorHeader[] = [
  { id: "ph", displayName: "pH", name: "pH" },
  {
    id: "availablePhosphorus",
    displayName: "Available Phosphorus",
    name: "P",
  },
  {
    id: "availablePotassium",
    displayName: "Available Potassium",
    name: "K",
  },
  {
    id: "availableMagnesium",
    displayName: "Available Magnesium",
    name: "Mg",
  },
];

const Page3Table: React.FC<Page3TableProps> = ({
  allTests,
  indicatorNamesAndIds,
  headers: visibleHeaders = page3Headers,
}) => {
  const gradient = tinygradient(IndicatorGradientColors);

  return (
    <div className={styles.reportTableContainer}>
      <div className={styles.tableWrapper}>
        <table className={styles.infoTable}>
          <thead>
            <tr>
              {/* Test Info Block Headers */}
              <th rowSpan={2} className={styles.tableHeader}>
                Sample Name
              </th>
              <th rowSpan={2} className={styles.tableHeader}>
                Farm
              </th>
              <th rowSpan={2} className={styles.tableHeader}>
                Field
              </th>
              <th rowSpan={2} className={styles.tableHeader}>
                What3Words
              </th>
              <th rowSpan={2} className={styles.tableHeader}>
                Date of Test
              </th>

              {/* Page 3 Headers */}
              {visibleHeaders.map((header) => (
                <th key={header.id} className={styles.subHeader} colSpan={2}>
                  {header.displayName}
                </th>
              ))}
            </tr>
            <tr>
              {/* Subheaders for Page 3 */}
              {visibleHeaders.map((header) => (
                <>
                  <th key={`${header.id}-value`} className={styles.subHeader}>
                    Value in {header.id === "ph" ? "µgC/gₛₒᵢₗ" : "mg/L"}
                  </th>
                  <th key={`${header.id}-index`} className={styles.subHeader}>
                    {header.id === "ph" ? "PES index" : "RB209 index"}
                  </th>
                </>
              ))}
            </tr>
          </thead>
          <tbody>
            {allTests.map((test, index) => (
              <tr key={test.id} className={styles.tableRow}>
                {/* Render TestInfoBlock inline */}
                <td className={styles.tableCell}>{test.name}</td>
                <td className={styles.tableCell}>{test.farm?.name}</td>
                <td className={styles.tableCell}>{test.field?.name}</td>
                <td className={`${styles.tableCell} ${styles.noBreak}`}>
                  {test.location
                    ? `${test.location.what3Words?.words ?? "Unknown"}`
                    : "N/A"}
                </td>
                <td className={styles.tableCell}>
                  {test.createdAt
                    ? format(parseISO(test.createdAt), "dd/MM/yyyy")
                    : "N/A"}
                </td>

                {/* Render Page 3 Values in the same row */}
                {visibleHeaders.map((header) => {
                  const { value } = GetDynamicValue(test, header.name);
                  const trainedIndicator = indicatorNamesAndIds.find(
                    (i) => i.name === header.name
                  )?.trainedIndicator;
                  const pesIndex = GetPESIndex(value, trainedIndicator);
                  const index = pesIndex
                    ? pesIndex.indices.names[pesIndex.indexValue]
                    : "N/A";
                  const divisor = pesIndex
                    ? pesIndex.indices.names.length - 1
                    : 0;
                  const gradientPosition = Math.min(
                    Math.max(
                      0,
                      divisor === 0 || !pesIndex
                        ? 0
                        : pesIndex.indexValue / divisor
                    ),
                    1
                  );
                  const indexColor =
                    index === "N/A"
                      ? undefined
                      : gradient
                          .rgbAt(gradientPosition)
                          .setAlpha(0.7)
                          .toHex8String();

                  return (
                    <React.Fragment key={header.id}>
                      {test.isFailure ? (
                        <>
                          <td className={styles.tableCell}>N/A</td>
                          <td className={styles.tableCell}>N/A</td>
                        </>
                      ) : (
                        <>
                          <td
                            key={`${header.id}-value`}
                            className={styles.tableCell}
                          >
                            {typeof value === "number"
                              ? value.toFixed(
                                  trainedIndicator?.indicatorDefinition
                                    ?.displayDecimalPlaces ?? 2
                                )
                              : value}
                          </td>
                          <td
                            key={`${header.id}-index`}
                            className={`${styles.tableCell} ${
                              indexColor &&
                              !gradient.rgbAt(gradientPosition).isLight()
                                ? styles.light
                                : ""
                            }`}
                            style={{ backgroundColor: indexColor }}
                          >
                            {index}
                          </td>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Page3Table;
